// src/components/super-admin/SuperAdminLayout.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AcademicCapIcon,
  BanknotesIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  HomeIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { showConfirm } from "../../utils/alert";

const navigation = [
  { label: "Dashboard", to: "/super-admin/dashboard", icon: HomeIcon },
  { label: "Schools", to: "/super-admin/schools", icon: BuildingOffice2Icon },
  { label: "Subscriptions", to: "/super-admin/subscriptions", icon: BanknotesIcon },
  { label: "Finance", to: "/super-admin/finance", icon: ChartBarSquareIcon },
  { label: "MBSSE Calendar", to: "/super-admin/academic-calendar", icon: CalendarDaysIcon },
  { label: "Profile", to: "/super-admin/profile", icon: UserCircleIcon },
  { label: "Settings", to: "/super-admin/settings", icon: Cog6ToothIcon },
];

const initials = (user) =>
  [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((value) => value[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "SA";

export default function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeProfile = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", closeProfile);
    return () => document.removeEventListener("mousedown", closeProfile);
  }, []);

  const signOut = async () => {
    const confirmed = await showConfirm({
      title: "Sign Out",
      text: "Do you want to leave the Super Admin console?",
      confirmText: "Sign out",
      icon: "question",
    });
    if (!confirmed) return;
    logout();
  };

  const SidebarContent = () => (
    <>
      <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-5">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
          <AcademicCapIcon className="h-6 w-6" />
        </span>
        <div>
          <p className="font-bold text-slate-900">PawaSchool</p>
          <p className="text-[11px] font-medium text-slate-400">Super Admin Console</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1.5 overflow-y-auto p-3">
        {navigation.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive
                  ? "bg-primary-50 text-primary-700"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-200 p-4">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs font-semibold text-slate-700">Platform control</p>
          <p className="mt-1 text-[11px] leading-4 text-slate-400">
            School records remain separated and private.
          </p>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <aside className="relative flex h-full w-[86%] max-w-xs flex-col bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-lg p-2 text-slate-400 hover:bg-slate-100"
              aria-label="Close navigation menu"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="rounded-xl border border-slate-200 p-2 text-slate-600 lg:hidden"
                aria-label="Open navigation"
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900 sm:text-base">
                  Platform Administration
                </p>
                <p className="hidden text-xs text-slate-400 sm:block">
                  Manage the complete PawaSchool network
                </p>
              </div>
            </div>

            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setProfileOpen((value) => !value)}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1.5 pr-2.5 transition hover:border-primary-200 hover:bg-primary-50"
                aria-expanded={profileOpen}
              >
                {user?.profile_picture ? (
                  <img src={user.profile_picture} alt="Profile" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 text-xs font-bold text-white">
                    {initials(user)}
                  </span>
                )}
                <div className="hidden min-w-0 text-left sm:block">
                  <p className="max-w-[150px] truncate text-xs font-bold text-slate-800">
                    {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Super Admin"}
                  </p>
                  <p className="text-[10px] text-slate-400">Super Administrator</p>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-slate-400" />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/70">
                  <div className="border-b border-slate-100 px-3 py-2.5">
                    <p className="truncate text-sm font-bold text-slate-900">
                      {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Super Admin"}
                    </p>
                    <p className="truncate text-xs text-slate-400">{user?.email}</p>
                  </div>
                  <button type="button" onClick={() => navigate("/super-admin/profile")} className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    <UserCircleIcon className="h-5 w-5" /> My profile
                  </button>
                  <button type="button" onClick={() => navigate("/super-admin/settings")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    <Cog6ToothIcon className="h-5 w-5" /> Settings
                  </button>
                  <button type="button" onClick={signOut} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
                    <XMarkIcon className="h-5 w-5" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}